import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { BackupContents, BackupList, BackupRestored } from '@one-deployment/api'
import { Badge, Button, Card, Input, Label, PageHeader, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Checkbox } from '@/components/shadcn/checkbox'
import { EmptyState, ErrorState } from '@/components/status'
import { get, post } from '@/lib/api'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'

/**
 * Restore secrets from the nightly backup (features/23). Every open and restore
 * asks for a fresh authenticator code and emails every admin, because an admin
 * login alone is then enough to read a backup. Values never reach this page.
 */
export function BackupsPage() {
  const list = useQuery({ queryKey: qk.backups(), queryFn: ({ signal }) => get<BackupList>('/api/admin/backups', signal) })
  const [key, setKey] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [opened, setOpened] = useState<BackupContents | null>(null)
  const [pick, setPick] = useState<Record<string, Set<string>>>({})
  const [done, setDone] = useState<string[] | null>(null)
  const open = useMutation({
    mutationFn: (k: string) => post<BackupContents>('/api/admin/backups/open', { key: k, code }),
    onSuccess: (c) => { setOpened(c); setCode(''); setPick({}); setDone(null) },
  })
  const restore = useMutation({
    mutationFn: (projectId: string) => post<BackupRestored>('/api/admin/backups/restore', { key: opened!.key, code, projectId, names: [...(pick[projectId] ?? [])] }),
    onSuccess: (r) => { setDone(r.restored); setCode(''); setPick({}) },
  })
  const toggle = (pid: string, name: string) => setPick((p) => {
    const s = new Set(p[pid] ?? [])
    if (s.has(name)) s.delete(name); else s.add(name)
    return { ...p, [pid]: s }
  })
  const codeInput = (
    <div className="w-40"><Label htmlFor="bk-code">Authenticator code</Label><Input id="bk-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></div>
  )

  if (list.isPending) return <div className="p-5"><SkeletonRows rows={6} /></div>
  if (list.error) return <ErrorState error={list.error} retry={() => void list.refetch()} />
  const l = list.data
  return (
    <div className="space-y-4 px-6 py-5">
      <PageHeader title="Backups" meta="Put project secrets back as they were in a nightly backup. Every admin gets an email each time a backup is opened." />
      {!l.ready && <p className="rounded-lg border border-border bg-surface-2 px-4 py-3 text-sm text-fg-muted">Not set up yet. Missing: {l.missing.join(', ')}. See docs/runbooks.</p>}
      {opened ? (
        <Card>
          <div className="mb-3 flex items-center justify-between"><h2 className="font-medium">{opened.key}</h2><Button onClick={() => setOpened(null)}>Back</Button></div>
          {done && <p className="mb-3 text-ok">Restored: {done.join(', ') || 'nothing'}. Boxes get the new values now.</p>}
          {opened.projects.length === 0 && <p>No secrets in this backup.</p>}
          {opened.projects.map((p) => (
            <div key={p.projectId} className="mb-4">
              <h3 className="mb-1 font-medium">{p.key}{!p.exists && <Badge tone="warn">project gone</Badge>}</h3>
              <Table head={["", "Secret", "Saved", "Now"]}>
                  {p.secrets.map((s) => (
                    <Tr key={s.name}>
                      <Td><Checkbox aria-label={`Restore ${s.name}`} disabled={!p.exists || s.now === 'same'} checked={pick[p.projectId]?.has(s.name) ?? false} onCheckedChange={() => toggle(p.projectId, s.name)} /></Td>
                      <Td><code>{s.name}</code></Td>
                      <Td>{dateTime(s.updatedAt)}</Td>
                      <Td>{s.now === 'same' ? <Badge>same as now</Badge> : s.now === 'different' ? <Badge tone="warn">changed since</Badge> : <Badge tone="err">deleted since</Badge>}</Td>
                    </Tr>
                  ))}
              </Table>
              {(pick[p.projectId]?.size ?? 0) > 0 && (
                <div className="mt-2 flex items-end gap-2">{codeInput}<Button variant="primary" disabled={code.length !== 6 || restore.isPending} onClick={() => restore.mutate(p.projectId)}>Restore {pick[p.projectId]!.size}</Button></div>
              )}
            </div>
          ))}
          {restore.error && <ErrorState error={restore.error} compact />}
        </Card>
      ) : l.backups.length === 0 ? (
        <EmptyState title="No backups yet" body="The nightly backup job writes one a day." />
      ) : (
        <Card>
          <Table head={["Backup", "Made", "Size", ""]}>
              {l.backups.map((b) => (
                <Tr key={b.key}>
                  <Td><code>{b.key}</code></Td>
                  <Td>{dateTime(b.uploadedAt)}</Td>
                  <Td>{(b.size / 1e6).toFixed(1)} MB</Td>
                  <Td><Button disabled={!l.ready} onClick={() => setKey(b.key)}>Open</Button></Td>
                </Tr>
              ))}
          </Table>
          {key && (
            <div className="mt-3 flex items-end gap-2">{codeInput}<Button variant="primary" disabled={code.length !== 6 || open.isPending} onClick={() => open.mutate(key)}>Open {key}</Button></div>
          )}
          {open.error && <ErrorState error={open.error} compact />}
        </Card>
      )}
    </div>
  )
}
